import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getLayout } from '../../api';
import { useTailwindCdn } from '../../useTailwindCdn';
import {
  GRID_SIZE,
  DEFAULT_NAMING,
  displaySeatLabel,
  getSeatTypeInfo,
  hydrateSeatsFromApi,
} from '../../create/components/layouteditor/utils/seatUtils';
import '../../layouts-v2.css';

const getZonePoly = (zone) => {
  if (!zone) return null;
  if (Array.isArray(zone.polygonPoints) && zone.polygonPoints.length >= 3) {
    return zone.polygonPoints;
  }
  if (Array.isArray(zone.points) && zone.points.length >= 3) {
    return zone.points;
  }
  return null;
};

const zoneSeatCount = (seats, zoneId) => (seats || []).filter((s) => s.zoneId === zoneId).length;

const zoneCenter = (zone) => {
  const poly = getZonePoly(zone);
  if (poly && poly.length >= 3) {
    return {
      x: poly.reduce((s, p) => s + p.x, 0) / poly.length,
      y: poly.reduce((s, p) => s + p.y, 0) / poly.length,
    };
  }
  return {
    x: (zone.x || 0) + (zone.width || 0) / 2,
    y: (zone.y || 0) + (zone.height || 0) / 2,
  };
};

export function LayoutsV2View() {
  useTailwindCdn();
  const navigate = useNavigate();
  const { id } = useParams();
  const [layout, setLayout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);
  const [zoom, setZoom] = useState(0.85);
  const [offset, setOffset] = useState({ x: 24, y: 24 });
  const [panning, setPanning] = useState(false);
  const panRef = useRef(null);
  const viewportRef = useRef(null);
  const didPanRef = useRef(false);

  useEffect(() => {
    if (id) fetchLayout();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLayout = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getLayout(id);
      setLayout(data);
      setSelectedZone(null);
    } catch (err) {
      console.error('Error fetching layout:', err);
      setError(err.message || 'Layout bulunamadı');
    } finally {
      setLoading(false);
    }
  };

  const naming = useMemo(
    () => ({
      ...DEFAULT_NAMING,
      ...(layout?.settings?.naming || {}),
      aisleCols: [...(layout?.settings?.naming?.aisleCols || [])],
      aisleCells: [...(layout?.settings?.naming?.aisleCells || [])],
      voidCols: [...(layout?.settings?.naming?.voidCols || [])],
      voidRows: [...(layout?.settings?.naming?.voidRows || [])],
    }),
    [layout],
  );

  const seats = useMemo(() => hydrateSeatsFromApi(layout?.seats || []), [layout]);
  const zones = layout?.zones || [];
  const backgroundImage = layout?.settings?.backgroundImage || null;
  const hasZones = zones.length > 0;

  const visibleSeats = useMemo(() => {
    // Overview: no seats. Zone page: only that zone's seats.
    // No zones at all: show every seat.
    if (!hasZones) return seats;
    if (!selectedZone) return [];
    return seats.filter((s) => s.zoneId === selectedZone.id);
  }, [seats, hasZones, selectedZone]);

  const canvasSize = useMemo(() => {
    let maxX = 40 * GRID_SIZE;
    let maxY = 30 * GRID_SIZE;
    const considerSeats = hasZones && !selectedZone ? [] : visibleSeats;
    considerSeats.forEach((s) => {
      const x = (Number.isFinite(s.x) ? s.x : (s.col || 0) * GRID_SIZE) + GRID_SIZE;
      const y = (Number.isFinite(s.y) ? s.y : (s.row || 0) * GRID_SIZE) + GRID_SIZE;
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    });
    zones.forEach((z) => {
      const poly = getZonePoly(z);
      if (poly) {
        poly.forEach((p) => {
          maxX = Math.max(maxX, p.x + GRID_SIZE);
          maxY = Math.max(maxY, p.y + GRID_SIZE);
        });
      } else {
        maxX = Math.max(maxX, (z.x || 0) + (z.width || 0) + GRID_SIZE);
        maxY = Math.max(maxY, (z.y || 0) + (z.height || 0) + GRID_SIZE);
      }
    });
    return {
      width: Math.ceil(maxX / GRID_SIZE) * GRID_SIZE + GRID_SIZE * 2,
      height: Math.ceil(maxY / GRID_SIZE) * GRID_SIZE + GRID_SIZE * 2,
    };
  }, [visibleSeats, zones, hasZones, selectedZone]);

  const enterZone = (zone) => {
    if (!zone || zone.isStage || zone.name === 'SAHNE') return;
    setSelectedZone(zone);
    const c = zoneCenter(zone);
    const rect = viewportRef.current?.getBoundingClientRect();
    if (rect) {
      setZoom(1);
      setOffset({
        x: rect.width / 2 - c.x * 1 - 20,
        y: rect.height / 2 - c.y * 1 - 40,
      });
    }
  };

  const exitZone = () => {
    setSelectedZone(null);
    setZoom(0.85);
    setOffset({ x: 24, y: 24 });
  };

  const handlePanStart = (e) => {
    if (e.button !== 0 && e.button !== 1) return;
    // Don't start pan when clicking a zone path (handled separately)
    if (e.target?.closest?.('[data-zone-hit]')) return;
    didPanRef.current = false;
    setPanning(true);
    panRef.current = {
      x: e.clientX,
      y: e.clientY,
      ox: offset.x,
      oy: offset.y,
    };
  };

  const handlePanMove = (e) => {
    if (!panning || !panRef.current) return;
    const dx = e.clientX - panRef.current.x;
    const dy = e.clientY - panRef.current.y;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) didPanRef.current = true;
    setOffset({
      x: panRef.current.ox + dx,
      y: panRef.current.oy + dy,
    });
  };

  const handlePanEnd = () => {
    setPanning(false);
    panRef.current = null;
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.08 : 0.08;
    setZoom((z) => Math.min(2.5, Math.max(0.25, z + delta)));
  };

  if (loading) {
    return (
      <div className="layouts-v2-root space-y-6 mt-4">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4" />
          <div className="h-[70vh] bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="layouts-v2-root space-y-6 mt-4">
        <button
          type="button"
          onClick={() => navigate('/layouts-v2')}
          className="inline-flex items-center px-4 py-2 text-gray-600 hover:text-gray-900"
        >
          Layoutlara Dön
        </button>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h3 className="text-lg font-semibold text-red-900 mb-2">Layout yüklenirken hata</h3>
          <p className="text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  if (!layout) {
    return (
      <div className="layouts-v2-root space-y-6 mt-4">
        <button
          type="button"
          onClick={() => navigate('/layouts-v2')}
          className="inline-flex items-center px-4 py-2 text-gray-600 hover:text-gray-900"
        >
          Layoutlara Dön
        </button>
        <div className="text-center py-16">
          <h3 className="text-xl font-semibold text-gray-900 mb-3">Layout bulunamadı</h3>
        </div>
      </div>
    );
  }

  const showZoneOverview = hasZones && !selectedZone;
  const showZonePage = hasZones && selectedZone;

  return (
    <div className="layouts-v2-root flex flex-col mt-2" style={{ height: 'calc(100vh - 6rem)' }}>
      <div className="flex-shrink-0 flex items-center justify-between gap-3 px-1 pb-3">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => navigate('/layouts-v2')}
            className="text-sm text-gray-600 hover:text-gray-900 whitespace-nowrap"
          >
            ← Layoutlar
          </button>
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-gray-900 truncate">{layout.name}</h1>
            <p className="text-xs text-gray-500 truncate">
              {layout.venue?.name || 'Mekan yok'}
              {layout.venue?.city ? ` · ${layout.venue.city}` : ''}
              {' · '}
              {seats.length} koltuk
              {zones.length ? ` · ${zones.length} zone` : ''}
              {selectedZone ? ` · Sayfa: ${selectedZone.name}` : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {showZonePage && (
            <button
              type="button"
              onClick={exitZone}
              className="px-3 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-800 hover:bg-gray-200"
            >
              ← Genel bakış
            </button>
          )}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
            <button
              type="button"
              onClick={() => setZoom((z) => Math.max(0.25, z - 0.1))}
              className="w-8 h-8 rounded-md hover:bg-white text-sm"
            >
              −
            </button>
            <button
              type="button"
              onClick={() => {
                setZoom(showZonePage ? 1 : 0.85);
                setOffset({ x: 24, y: 24 });
              }}
              className="min-w-[3rem] h-8 px-1 rounded-md text-[11px] tabular-nums hover:bg-white"
            >
              {Math.round(zoom * 100)}%
            </button>
            <button
              type="button"
              onClick={() => setZoom((z) => Math.min(2.5, z + 0.1))}
              className="w-8 h-8 rounded-md hover:bg-white text-sm"
            >
              +
            </button>
          </div>
          <button
            type="button"
            onClick={() => navigate(`/layouts-v2/${id}`)}
            className="inline-flex items-center px-4 py-2 bg-[#AE256D] text-white rounded-lg hover:bg-[#8B1E55] text-sm font-medium"
          >
            Düzenle
          </button>
        </div>
      </div>

      <div
        ref={viewportRef}
        className="flex-1 min-h-0 relative rounded-xl border border-gray-200 bg-[#e8eef3] overflow-hidden select-none"
        style={{ cursor: panning ? 'grabbing' : 'grab' }}
        onMouseDown={handlePanStart}
        onMouseMove={handlePanMove}
        onMouseUp={handlePanEnd}
        onMouseLeave={handlePanEnd}
        onWheel={handleWheel}
      >
        <div
          className="absolute origin-top-left"
          style={{
            width: canvasSize.width,
            height: canvasSize.height,
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
            backgroundImage: showZoneOverview
              ? 'none'
              : `
              linear-gradient(to right, rgba(209,213,219,0.55) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(209,213,219,0.55) 1px, transparent 1px)
            `,
            backgroundSize: `${GRID_SIZE}px ${GRID_SIZE}px`,
            backgroundColor: backgroundImage
              ? 'transparent'
              : showZoneOverview
                ? '#e8eef3'
                : '#fff',
            border: '2px solid #d1d5db',
          }}
        >
          {backgroundImage && !showZoneOverview && (
            <img
              src={backgroundImage}
              alt=""
              draggable={false}
              className="absolute inset-0 w-full h-full object-contain pointer-events-none"
              style={{ opacity: 0.85, zIndex: 0 }}
            />
          )}

          {/* Overview only: zone shapes. Zone page = seats only, no zone outline. */}
          {showZoneOverview && zones.length > 0 && (
            <svg
              className="absolute inset-0"
              width="100%"
              height="100%"
              style={{
                zIndex: 2,
                overflow: 'visible',
                pointerEvents: 'auto',
              }}
            >
              {zones.map((zone) => {
                if (!zone) return null;
                const count = zoneSeatCount(seats, zone.id);
                const isStage = !!zone.isStage || zone.name === 'SAHNE';
                const poly = getZonePoly(zone);
                const label = isStage ? zone.name : `${zone.name} · ${count} koltuk`;

                const onZoneClick = (e) => {
                  e.stopPropagation();
                  if (didPanRef.current) return;
                  if (!isStage) enterZone(zone);
                };

                if (poly && poly.length >= 3) {
                  const d =
                    poly.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';
                  const cx = poly.reduce((s, p) => s + p.x, 0) / poly.length;
                  const cy = poly.reduce((s, p) => s + p.y, 0) / poly.length;
                  return (
                    <g key={zone.id}>
                      <path
                        data-zone-hit="1"
                        d={d}
                        fill={zone.color || '#6B7280'}
                        fillOpacity={isStage ? 0.55 : 0.82}
                        stroke="rgba(0,0,0,0.35)"
                        strokeWidth={2.5}
                        style={{
                          cursor: isStage ? 'default' : 'pointer',
                          filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.25))',
                        }}
                        onClick={onZoneClick}
                      />
                      <text
                        x={cx}
                        y={cy}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill="#fff"
                        fontWeight="700"
                        fontSize={Math.max(12, Math.min(18, (zone.width || 120) / 7))}
                        paintOrder="stroke"
                        stroke="rgba(0,0,0,0.45)"
                        strokeWidth="3"
                        style={{ pointerEvents: 'none', userSelect: 'none' }}
                      >
                        {label}
                      </text>
                    </g>
                  );
                }

                const x = zone.x || 0;
                const y = zone.y || 0;
                const w = zone.width || 100;
                const h = zone.isCircle ? w : zone.height || 80;
                return (
                  <g key={zone.id}>
                    {zone.isCircle || zone.type === 'CIRCLE' ? (
                      <ellipse
                        data-zone-hit="1"
                        cx={x + w / 2}
                        cy={y + h / 2}
                        rx={w / 2}
                        ry={h / 2}
                        fill={zone.color || '#6B7280'}
                        fillOpacity={isStage ? 0.55 : 0.82}
                        stroke="rgba(0,0,0,0.35)"
                        strokeWidth={2.5}
                        style={{ cursor: isStage ? 'default' : 'pointer' }}
                        onClick={onZoneClick}
                      />
                    ) : (
                      <rect
                        data-zone-hit="1"
                        x={x}
                        y={y}
                        width={w}
                        height={h}
                        rx={isStage ? 8 : 6}
                        fill={zone.color || '#6B7280'}
                        fillOpacity={isStage ? 0.55 : 0.82}
                        stroke="rgba(0,0,0,0.35)"
                        strokeWidth={2.5}
                        style={{ cursor: isStage ? 'default' : 'pointer' }}
                        onClick={onZoneClick}
                      />
                    )}
                    <text
                      x={x + w / 2}
                      y={y + h / 2}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fill="#fff"
                      fontWeight="700"
                      fontSize={Math.max(12, Math.min(18, w / 8))}
                      paintOrder="stroke"
                      stroke="rgba(0,0,0,0.45)"
                      strokeWidth="3"
                      style={{ pointerEvents: 'none', userSelect: 'none' }}
                    >
                      {label}
                    </text>
                  </g>
                );
              })}
            </svg>
          )}

          {/* Seats only on zone page (or when layout has no zones) */}
          {visibleSeats.map((seat) => {
            const left = Number.isFinite(seat.x) ? seat.x : (seat.col || 0) * GRID_SIZE;
            const top = Number.isFinite(seat.y) ? seat.y : (seat.row || 0) * GRID_SIZE;
            const zone = zones.find((z) => z.id === seat.zoneId);
            const typeInfo = getSeatTypeInfo(seat.seatType);
            const label = displaySeatLabel(seat, naming);
            return (
              <div
                key={seat.id}
                className="absolute rounded-lg flex items-center justify-center text-white font-bold border-2 pointer-events-none"
                style={{
                  left: left + 2,
                  top: top + 2,
                  width: GRID_SIZE - 4,
                  height: GRID_SIZE - 4,
                  backgroundColor: zone?.color || typeInfo.color,
                  borderColor: zone?.color || typeInfo.color,
                  fontSize: '10px',
                  zIndex: 10,
                  transform: seat.rotation ? `rotate(${seat.rotation}deg)` : undefined,
                }}
                title={label}
              >
                {label}
              </div>
            );
          })}

          {showZonePage && visibleSeats.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="bg-white/90 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-600 shadow">
                Bu zone’da henüz koltuk yok
              </div>
            </div>
          )}
        </div>

        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between pointer-events-none">
          <span className="text-[11px] bg-white/90 border border-gray-200 rounded-lg px-2.5 py-1 text-gray-600 shadow-sm">
            {showZoneOverview
              ? 'Zone’a tıkla = koltuk sayfası · Sürükle = gez'
              : 'Sürükle = gez · Tekerlek = zoom · ← Genel bakış'}
          </span>
          <span className="text-[11px] bg-white/90 border border-gray-200 rounded-lg px-2.5 py-1 text-gray-600 shadow-sm tabular-nums">
            {showZonePage
              ? `${visibleSeats.length} koltuk · ${selectedZone.name}`
              : `${seats.length} koltuk${zones.length ? ` · ${zones.length} zone` : ''}`}
          </span>
        </div>
      </div>
    </div>
  );
}

export default LayoutsV2View;
